import {Trans} from '@lingui/macro'
import {Link as RouterLink} from 'react-router-dom'
import styled from 'styled-components'

import {Box, Inline, Link, Stack, Text, tokens} from '@pleo-io/telescope'
import {CirclePlus} from '@pleo-io/telescope-icons'

import {pxBreakpoints} from '@product-web/styles/theme'
import {useContainerQuery} from '@product-web/telescope-lab/container-queries/container'

import {bffHooks} from '../../bff-hooks'

interface VendorBoxProps {
    vendorName?: string | undefined
    vendorLogoUrl?: string | undefined
}

export const VendorBox = ({vendorName, vendorLogoUrl}: VendorBoxProps) => {
    const isEmpty = !vendorName

    return (
        <Link as={RouterLink}>
            <StyledBox border={tokens.borderInteractiveQuiet} borderRadius={8} isEmpty={isEmpty}>
                {isEmpty ? (
                    <EmptyVendorBox />
                ) : (
                    <Inline alignItems={'center'} space={16}>
                        <img height={40} width={40} src={vendorLogoUrl} alt="vendorName" />
                        <Text variant="large-accent">{vendorName}</Text>
                    </Inline>
                )}
            </StyledBox>
        </Link>
    )
}

const EmptyVendorBox = () => {
    return (
        <Inline alignItems={'center'} space={16}>
            <CirclePlus color={tokens.colorContentInteractiveQuiet} size={24} />
            <Text>
                <Trans>New Vendor</Trans>
            </Text>
        </Inline>
    )
}

const StyledBox = styled(Box)<{isEmpty?: boolean}>`
    max-width: 345px;
    height: 88px;
    display: flex;
    border-style: ${({isEmpty}) => (isEmpty ? 'dashed' : '')};
    justify-content: ${({isEmpty}) => (isEmpty ? 'center' : 'flex-start')};
    padding-left: ${({isEmpty}) => (isEmpty ? 0 : tokens.spacing24)};
`

export const VendorList = () => {
    const {data: vendors} =
        bffHooks.vendorCards.spendActivationTemplates.getPopularVendors.useQuery()

    const isDesktopMed = useContainerQuery({
        name: 'main',
        minWidth: pxBreakpoints.desktopMedUp,
    })

    if (!vendors) {
        return null
    }

    // These vendors will be changed to be a static list in https://linear.app/pleo/issue/GAIA-1688/update-recommended-vendor-cards-page-pre-fill-vendor-card-creation
    // e.g. Adobe, ChatGPT, Microsoft, Apple
    const limitVendors = vendors.slice(0, 4)

    return (
        <ContainerStack isDesktop={isDesktopMed}>
            {limitVendors.map(({vendorId, vendorName, signedLogoUrl}) => (
                <VendorBox key={vendorId} vendorName={vendorName} vendorLogoUrl={signedLogoUrl} />
            ))}
        </ContainerStack>
    )
}

const ContainerStack = styled(Stack)<{isDesktop: boolean}>`
    justify-items: stretch;
    gap: ${tokens.spacing16};
    grid-template-columns: ${({isDesktop}) => (isDesktop ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)')};
`
